// resolveOwnerKeys.ts — Sprint 107 (ADR-100): one owner-identity resolver.
// ----------------------------------------------------------------------------
// Before this, owner-scoped row matching in the Atrium homes used TWO competing
// conventions kept aligned only by luck:
//   * AtriumOwnerHome resolved `user.ownerId ?? user.id` (a NUMERIC Owner.id
//     fallback) for maintenance rows, and
//   * condoStore matched fees/payments by `fee.ownerId === user.id` (the UUID).
//
// Sprint 107 collapses the backend to a single canonical owner key: the
// login-user UUID (apps.users.user_id == User.id == Fee.ownerId ==
// Payment.ownerId == MaintenanceRequest.ownerId). The login response's
// `ownerId` now ALSO carries that same UUID (AuthService change), so the old
// numeric `?? user.id` disjunction is obsolete.
//
// `resolveOwnerKeys(user)` returns the single canonical key every owner-scoped
// tile should match on. `key` is the value to compare row.ownerId against;
// `keys` is a de-duplicated set (canonical + any legacy aliases the auth user
// still exposes) for defensive matching during the transition — but it
// deliberately NO LONGER includes a numeric Owner.id fallback.

export interface OwnerIdentityLike {
  id?: string | null;
  /** Post-Sprint-107 this equals `id` (the login-user UUID). */
  ownerId?: string | null;
  email?: string | null;
}

export interface ResolvedOwnerKeys {
  /** The single canonical owner key = the login-user UUID. */
  key: string;
  /** Canonical + legacy aliases (no numeric fallback) for row matching. */
  keys: string[];
  /** Convenience for document/email-keyed lookups. */
  email: string | null;
}

export function resolveOwnerKeys(user: OwnerIdentityLike | null | undefined): ResolvedOwnerKeys {
  const canonical = user?.id ?? '';
  // ownerId now mirrors id post-107; keep it in the alias set only so rows
  // created under the old login response (ownerId === user UUID) still match.
  // Any falsy / numeric-Owner.id legacy value is filtered out by the dedupe +
  // the canonical-first ordering: callers compare against `key` first.
  const aliases = [canonical, user?.ownerId ?? '']
    .filter((k): k is string => typeof k === 'string' && k.length > 0);

  return {
    key: canonical,
    keys: Array.from(new Set(aliases)),
    email: user?.email ?? null,
  };
}

/** True when a row's ownerId belongs to the given user (canonical match). */
export function isOwnRow(
  rowOwnerId: string | null | undefined,
  user: OwnerIdentityLike | null | undefined,
): boolean {
  if (!rowOwnerId) return false;
  return resolveOwnerKeys(user).keys.includes(rowOwnerId);
}

// ── Owner-RECORD → canonical key (Sprint 136 / ADR-131) ────────────────────
//
// `resolveOwnerKeys` above answers "who am I?" from the logged-in user. This
// answers the other half: "which key identifies the Owner row I just picked
// out of a <Select>?" — the direction every management write surface needs.
//
// The trap: `Owner.id` is the owners-table NUMERIC primary key, while every
// persisted `ownerId` column (Fee, Payment, AmenityBooking, MaintenanceRequest)
// holds the login-user UUID (`Owner.userId` == `apps.users.user_id`, V31 /
// Sprint 107). The two are both "the owner's id" in English and neither is
// typed distinctly, so binding a Select straight to `o.id` type-checks, looks
// right, and silently writes a key nothing can ever match. That is exactly how
// R12-6 happened: a booking stamped `owner_id="51"` could never be cancelled by
// its owner, because the server compares against their UUID.

export interface OwnerRecordLike {
  id: string | number;
  /** The login-user UUID. Null when the owner has no linked login account. */
  userId?: string | null;
}

/**
 * The canonical key for a SELECTED owner record — use this for any value that
 * gets persisted as an `ownerId`, or compared against one.
 *
 * Falls back to the numeric id only when the owner has no linked login account.
 * That fallback is deliberate and load-bearing: it keeps an un-activated owner
 * selectable (a real case — see StatusCertificates' "not linked to a login
 * account" prefill branch) rather than binding `undefined`. Such a row still
 * won't match user-keyed lookups, but that is a data-completeness gap, not a
 * key-convention bug, and it stays visible instead of failing silently.
 */
export function resolveOwnerRecordKey(owner: OwnerRecordLike): string {
  return owner.userId ?? String(owner.id);
}
